import { useEffect, useState } from 'react';

import type { ContentfulAsset, ContentfulEntry } from '@order/Contentful';
import { useContentfulClient } from '@order/Contentful';

export const useUpgradeScreenLoadedContent = (
  tag?: SweetpassUpgradePageTag | '',
) => {
  const [content, setContent] = useState<SweetpassUpgradePage>();
  const [isLoading, setLoading] = useState(true);

  const contentfulClient = useContentfulClient();

  useEffect(() => {
    if (!contentfulClient || tag === '') {
      setLoading(false);

      return;
    }

    if (!tag) {
      return;
    }

    void contentfulClient
      .getEntries<ContentfulSweetpassUpgradePage>({
        content_type: 'sweetpassUpgradePage',
        'metadata.tags.sys.id[in]': tag,
        limit: 1,
        order: '-sys.updatedAt',
        include: 2,
      })
      .then(async (entries) => {
        if (!entries || entries.items.length === 0) return;

        setContent(upgradePageFromContentful(entries.items[0]));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contentfulClient, tag]);

  return { content, isLoading };
};

// ––– TYPES –––––––––––––––––––––––––

export type SweetpassUpgradePageTag =
  | 'join-sweetpass'
  | 'upgrade-to-sweetpass-plus';

export type SweetpassUpgradeCtaHandler = (
  action: SweetpassUpgradeCta['action'],
) => void;

// ––– Upgrade Page –––––––––––––––––––––––––

export type SweetpassUpgradePage = {
  readonly content: readonly SweetpassUpgradeContent[];
  readonly cta: SweetpassUpgradeCta;
};

type ContentfulSweetpassUpgradePage = {
  readonly content: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassUpgradeContent>
  >;
  readonly cta: ContentfulEntry<ContentfulSweetpassUpgradeCta>;
};

const upgradePageFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradePage>,
): SweetpassUpgradePage => {
  return {
    content: entry.fields.content.map(contentFromContentful),
    cta: ctaFromContentful(entry.fields.cta),
  };
};

// ––– Content (Header, Benefit List) ––––

export type SweetpassUpgradeContent =
  | SweetpassUpgradeHeader
  | SweetpassUpgradeBenefitList
  | SweetpassUpgradeUnknownContent;

type ContentfulSweetpassUpgradeContent =
  | ContentfulSweetpassUpgradeHeader
  | ContentfulSweetpassUpgradeBenefitList;

export type SweetpassUpgradeUnknownContent = {
  readonly type: string;
  readonly id: string;
};

const contentFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeContent>,
): SweetpassUpgradeContent => {
  const contentTypeId = entry.sys.contentType.sys.id;

  if (contentTypeId === 'sweetpassUpgradeHeader') {
    return headerFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassUpgradeHeader>,
    );
  }

  if (contentTypeId === 'sweetpassUpgradeBenefitList') {
    return benefitListFromContentful(
      entry as ContentfulEntry<ContentfulSweetpassUpgradeBenefitList>,
    );
  }

  return {
    type: contentTypeId,
    id: entry.sys.id,
  };
};

// ––– Header –––––––––––––––––––––––––––––––––––

export type SweetpassUpgradeHeader = {
  readonly type: 'sweetpassUpgradeHeader';
  readonly id: string;
  readonly heroImage: {
    readonly url: string;
    readonly description: string;
  };
  readonly headingText: string;
  readonly bodyText: string;
  readonly cta: SweetpassUpgradeCta;
};

type ContentfulSweetpassUpgradeHeader = {
  readonly heroImage: ContentfulAsset;
  readonly headingText: string;
  readonly bodyText: string;
  readonly cta: ContentfulEntry<ContentfulSweetpassUpgradeCta>;
};

const headerFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeHeader>,
): SweetpassUpgradeHeader => {
  return {
    type: 'sweetpassUpgradeHeader',
    id: entry.sys.id,
    heroImage: {
      url: entry.fields.heroImage.fields.file.url,
      description: entry.fields.heroImage.fields.description,
    },
    headingText: entry.fields.headingText,
    bodyText: entry.fields.bodyText,
    cta: ctaFromContentful(entry.fields.cta),
  };
};

// ––– Benefit List ––––––––––––––––––––––––––––

export type SweetpassUpgradeBenefitList = {
  readonly type: 'sweetpassUpgradeBenefitList';
  readonly id: string;
  readonly benefits: readonly SweetpassUpgradeBenefit[];
};

type ContentfulSweetpassUpgradeBenefitList = {
  readonly benefitList: ReadonlyArray<
    ContentfulEntry<ContentfulSweetpassUpgradeBenefit>
  >;
};

const benefitListFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeBenefitList>,
): SweetpassUpgradeBenefitList => ({
  type: 'sweetpassUpgradeBenefitList',
  id: entry.sys.id,
  benefits: entry.fields.benefitList.map(benefitFromContentful),
});

// ––– Benefit ––––––––––––––––––––––––––––––––––

export type SweetpassUpgradeBenefit = {
  readonly id: string;
  readonly image: {
    readonly url: string;
    readonly description: string;
  };
  readonly titleText: string;
  readonly bodyText: string;
};

type ContentfulSweetpassUpgradeBenefit = {
  readonly image: ContentfulAsset;
  readonly titleText: string;
  readonly bodyText: string;
};

const benefitFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeBenefit>,
): SweetpassUpgradeBenefit => ({
  id: entry.sys.id,
  image: {
    description: entry.fields.image.fields.description,
    url: entry.fields.image.fields.file.url,
  },
  titleText: entry.fields.titleText,
  bodyText: entry.fields.bodyText,
});

// ––– CTA ––––––––––––––––––––––––––––

export type SweetpassUpgradeCtaAction =
  | 'join_sweetpass'
  | 'upgrade_to_sweetpass_plus';

export type SweetpassUpgradeCta = {
  readonly title: string;
  readonly action: SweetpassUpgradeCtaAction;
};

type ContentfulSweetpassUpgradeCta = {
  readonly title: string;
  readonly action: string;
};

const ctaFromContentful = (
  entry: ContentfulEntry<ContentfulSweetpassUpgradeCta>,
): SweetpassUpgradeCta => ({
  action: entry.fields.action as SweetpassUpgradeCta['action'],
  title: entry.fields.title.replace('{cost}', '$10/mo.'),
});
